import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["step", "indicator", "line", "prevButton", "nextButton", "submitButton"];

  static values = {
    // Core behavior
    currentStep: { type: Number, default: 0 }, // Current active step index (0-based)
    validateOnNext: { type: Boolean, default: true }, // Whether to validate required fields before advancing
    allowSkip: { type: Boolean, default: false }, // Allow jumping to any step (bypasses visited-only restriction)
    scrollOnChange: { type: Boolean, default: false }, // Scroll to stepper top when changing steps
    saveProgress: { type: Boolean, default: true }, // Save form data to sessionStorage for persistence
    storageKey: { type: String, default: "stepper-progress" }, // sessionStorage key for saving progress

    // Styling: Default classes that can be overridden via data attributes
    indicatorBaseClass: { type: String, default: "" },
    indicatorCompleteClass: {
      type: String,
      default: "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 cursor-pointer hover:ring-2 hover:ring-black/5 dark:hover:ring-white/10"
    },
    indicatorCurrentClass: {
      type: String,
      default: "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 ring-2 ring-black/5 dark:ring-white/10 cursor-pointer"
    },
    indicatorVisitedClass: {
      type: String,
      default: "bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 border border-neutral-300 dark:border-neutral-600 cursor-pointer hover:bg-neutral-200 dark:hover:bg-neutral-700 hover:ring-2 hover:ring-black/5 dark:hover:ring-white/10"
    },
    indicatorUpcomingClass: {
      type: String,
      default: "bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500 border border-neutral-200 dark:border-neutral-700 cursor-not-allowed opacity-75"
    },
    lineBaseClass: { type: String, default: "" },
    lineCompleteClass: {
      type: String,
      default: "bg-neutral-900 dark:bg-white"
    },
    lineIncompleteClass: {
      type: String,
      default: "bg-neutral-200 dark:bg-neutral-700"
    },
  };

  connect() {
    this.formData = this.loadProgress() || {};
    this.visitedSteps = new Set([0]); // Track visited steps, starting with first step
    this.updateView();
    this.setupKeyboardNavigation();
  }

  setupKeyboardNavigation() {
    // Handle Enter key to navigate to next step
    this.element.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        const target = event.target;

        // Don't intercept Enter on textareas (allow new lines)
        if (target.tagName === 'TEXTAREA') {
          return;
        }

        // Don't intercept Enter on buttons (let them handle their own action)
        if (target.tagName === 'BUTTON') {
          return;
        }

        // For all other form elements (input, select), trigger next step or submit
        if (target.matches('input, select')) {
          event.preventDefault();

          // If we're on the last step, trigger submit
          if (this.currentStepValue === this.stepTargets.length - 1) {
            this.submit(event);
          } else {
            // Otherwise, move to next step
            this.next(event);
          }
        }
      }
    });
  }

  next(event) {
    event?.preventDefault();

    // Validate current step if validation is enabled
    if (this.validateOnNextValue && !this.validateCurrentStep()) {
      return;
    }

    // Save current step data
    if (this.saveProgressValue) {
      this.saveCurrentStepData();
    }

    // Move to next step if not at the end
    if (this.currentStepValue < this.stepTargets.length - 1) {
      this.currentStepValue++;
      this.visitedSteps.add(this.currentStepValue); // Mark new step as visited
      this.updateView();
      if (this.scrollOnChangeValue) this.scrollToTop();
    }
  }

  previous(event) {
    event?.preventDefault();

    // Save current step data even when going back
    if (this.saveProgressValue) {
      this.saveCurrentStepData();
    }

    // Move to previous step if not at the beginning
    if (this.currentStepValue > 0) {
      this.currentStepValue--;
      this.updateView();
      if (this.scrollOnChangeValue) this.scrollToTop();
    }
  }

  goToStep(event) {
    event?.preventDefault();
    const targetStep = parseInt(event.currentTarget.dataset.step);

    // Allow navigation to:
    // 1. Any visited step (includes previous steps and future steps that were already visited)
    // 2. Any step if allowSkip is enabled
    if (this.allowSkipValue || this.visitedSteps.has(targetStep)) {
      // Save current step data before navigating
      if (this.saveProgressValue) {
        this.saveCurrentStepData();
      }

      this.currentStepValue = targetStep;
      this.updateView();
      if (this.scrollOnChangeValue) this.scrollToTop();
    }
  }

  validateCurrentStep() {
    const currentStep = this.stepTargets[this.currentStepValue];
    if (!currentStep) return true;

    // Get all required inputs in the current step
    const requiredInputs = currentStep.querySelectorAll("input[required], select[required], textarea[required]");
    let isValid = true;

    requiredInputs.forEach((input) => {
      // Check if input is valid
      if (!input.checkValidity()) {
        isValid = false;
        // Show validation message
        input.reportValidity();
      }
    });

    // Dispatch custom event for additional validation
    const validateEvent = new CustomEvent("stepper:validate", {
      detail: { step: this.currentStepValue, isValid },
      bubbles: true,
      cancelable: true,
    });

    this.element.dispatchEvent(validateEvent);

    // If event was prevented, consider validation failed
    if (validateEvent.defaultPrevented) {
      isValid = false;
    }

    return isValid;
  }

  saveCurrentStepData() {
    const currentStep = this.stepTargets[this.currentStepValue];
    if (!currentStep) return;

    // Get all form inputs in the current step
    const inputs = currentStep.querySelectorAll("input, select, textarea");

    inputs.forEach((input) => {
      if (input.name) {
        if (input.type === "checkbox") {
          this.formData[input.name] = input.checked;
        } else if (input.type === "radio") {
          if (input.checked) {
            this.formData[input.name] = input.value;
          }
        } else {
          this.formData[input.name] = input.value;
        }
      }
    });

    // Save to sessionStorage if available
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(this.storageKeyValue, JSON.stringify(this.formData));
    }

    // Dispatch save event
    this.element.dispatchEvent(
      new CustomEvent("stepper:save", {
        detail: { step: this.currentStepValue, data: this.formData },
        bubbles: true,
      })
    );
  }

  loadProgress() {
    if (typeof sessionStorage !== "undefined") {
      const saved = sessionStorage.getItem(this.storageKeyValue);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          return {};
        }
      }
    }
    return {};
  }

  clearProgress() {
    this.formData = {};
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(this.storageKeyValue);
    }
  }

  updateView() {
    // Update step visibility
    this.stepTargets.forEach((step, index) => {
      if (index === this.currentStepValue) {
        step.classList.remove("hidden");
        step.classList.add("block");

        // Restore form data for this step
        this.restoreStepData(step);
      } else {
        step.classList.add("hidden");
        step.classList.remove("block");
      }
    });

    // Update indicators
    this.updateIndicators();

    // Update navigation buttons
    this.updateButtons();

    // Update review sections if they exist
    this.updateReviewSections();

    // Dispatch step change event
    this.element.dispatchEvent(
      new CustomEvent("stepper:change", {
        detail: {
          step: this.currentStepValue,
          totalSteps: this.stepTargets.length,
          isFirst: this.currentStepValue === 0,
          isLast: this.currentStepValue === this.stepTargets.length - 1,
        },
        bubbles: true,
      })
    );
  }

  restoreStepData(step) {
    const inputs = step.querySelectorAll("input, select, textarea");

    inputs.forEach((input) => {
      if (input.name && this.formData[input.name] !== undefined) {
        if (input.type === "checkbox") {
          input.checked = this.formData[input.name];
        } else if (input.type === "radio") {
          input.checked = input.value === this.formData[input.name];
        } else {
          input.value = this.formData[input.name];
        }
      }
    });
  }

  updateIndicators() {
    // Update indicator circles/buttons
    this.indicatorTargets.forEach((indicator, index) => {
      const state = this.getIndicatorState(index);

      // Update visual state
      this.applyIndicatorState(indicator, state);

      // Update aria attributes
      indicator.setAttribute("aria-current", state === 'current' ? "step" : "false");
    });

    // Update lines
    if (this.hasLineTarget) {
      this.lineTargets.forEach((line, index) => {
        this.applyLineState(line, index < this.currentStepValue);
      });
    }
  }

  getIndicatorState(index) {
    if (index < this.currentStepValue) return 'complete';
    if (index === this.currentStepValue) return 'current';
    if (this.visitedSteps.has(index)) return 'visited';
    return 'upcoming';
  }

  applyIndicatorState(indicator, state) {
    const numberSpan = indicator.querySelector('.step-number');
    const checkIcon = indicator.querySelector('.step-check');

    // Toggle number/check visibility
    if (state === 'complete') {
      numberSpan?.classList.add('hidden');
      checkIcon?.classList.remove('hidden');
    } else {
      numberSpan?.classList.remove('hidden');
      checkIcon?.classList.add('hidden');
    }

    // Remove all state classes from all states
    const allStateClasses = [
      this.indicatorCompleteClassValue,
      this.indicatorCurrentClassValue,
      this.indicatorVisitedClassValue,
      this.indicatorUpcomingClassValue
    ].join(' ').split(' ').filter(cls => cls);

    allStateClasses.forEach(cls => indicator.classList.remove(cls));

    // Add base classes if specified
    if (this.indicatorBaseClassValue) {
      this.indicatorBaseClassValue.split(' ').forEach(cls => {
        if (cls) indicator.classList.add(cls);
      });
    }

    // Add state-specific classes
    const stateClassMap = {
      complete: this.indicatorCompleteClassValue,
      current: this.indicatorCurrentClassValue,
      visited: this.indicatorVisitedClassValue,
      upcoming: this.indicatorUpcomingClassValue
    };

    const stateClasses = stateClassMap[state];
    if (stateClasses) {
      stateClasses.split(' ').forEach(cls => {
        if (cls) indicator.classList.add(cls);
      });
    }
  }

  applyLineState(line, isComplete) {
    // Remove all state classes
    const allStateClasses = [
      this.lineCompleteClassValue,
      this.lineIncompleteClassValue
    ].join(' ').split(' ').filter(cls => cls);

    allStateClasses.forEach(cls => line.classList.remove(cls));

    // Add base classes if specified
    if (this.lineBaseClassValue) {
      this.lineBaseClassValue.split(' ').forEach(cls => {
        if (cls) line.classList.add(cls);
      });
    }

    // Add state-specific classes
    const stateClass = isComplete ? this.lineCompleteClassValue : this.lineIncompleteClassValue;
    if (stateClass) {
      stateClass.split(' ').forEach(cls => {
        if (cls) line.classList.add(cls);
      });
    }
  }

  updateButtons() {
    const isFirstStep = this.currentStepValue === 0;
    const isLastStep = this.currentStepValue === this.stepTargets.length - 1;

    // Update previous button
    if (this.hasPrevButtonTarget) {
      this.prevButtonTargets.forEach((btn) => {
        if (isFirstStep) {
          btn.classList.add("hidden");
          btn.disabled = true;
        } else {
          btn.classList.remove("hidden");
          btn.disabled = false;
        }
      });
    }

    // Update next button
    if (this.hasNextButtonTarget) {
      this.nextButtonTargets.forEach((btn) => {
        if (isLastStep) {
          btn.classList.add("hidden");
        } else {
          btn.classList.remove("hidden");
        }
      });
    }

    // Update submit button
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTargets.forEach((btn) => {
        if (isLastStep) {
          btn.classList.remove("hidden");
        } else {
          btn.classList.add("hidden");
        }
      });
    }
  }

  scrollToTop() {
    // Scroll to the stepper container
    this.element.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  updateReviewSections() {
    // Update review fields
    this.element.querySelectorAll('[data-review-field]').forEach((field) => {
      const fieldName = field.dataset.reviewField;

      // Handle combined fields like "full_name"
      if (fieldName === 'full_name' && this.formData.first_name && this.formData.last_name) {
        field.textContent = `${this.formData.first_name} ${this.formData.last_name}`;
        return;
      }

      // Handle regular fields
      const value = this.formData[fieldName];
      field.textContent = (value !== undefined && value !== null && value !== '') ? value : '—';
    });

    // Update progress indicators
    this.updateProgressBar();
    this.updateStepCounter();
    this.updateStepLabel();
  }

  updateProgressBar() {
    const progressBar = this.element.querySelector('[data-stepper-progress-bar]');
    if (progressBar) {
      const percentage = ((this.currentStepValue + 1) / this.stepTargets.length) * 100;
      progressBar.style.width = `${percentage}%`;
    }
  }

  updateStepCounter() {
    const stepCounter = this.element.querySelector('[data-stepper-step-counter]');
    if (stepCounter) {
      stepCounter.textContent = `Step ${this.currentStepValue + 1} of ${this.stepTargets.length}`;
    }
  }

  updateStepLabel() {
    const stepLabel = this.element.querySelector('[data-stepper-step-label]');
    if (stepLabel?.dataset.stepLabels) {
      const labels = stepLabel.dataset.stepLabels.split(',');
      stepLabel.textContent = labels[this.currentStepValue] || `Step ${this.currentStepValue + 1}`;
    }
  }

  // Allow manual reset from outside
  reset() {
    this.currentStepValue = 0;
    this.clearProgress();
    this.updateView();
  }

  // Handle submit button click
  submit(event) {
    event?.preventDefault();

    // Validate final step if validation is enabled
    if (this.validateOnNextValue && !this.validateCurrentStep()) {
      return;
    }

    // Save final step data
    if (this.saveProgressValue) {
      this.saveCurrentStepData();
    }

    // Dispatch submit event for the form to handle
    const submitEvent = new CustomEvent("stepper:submit", {
      detail: { data: this.formData },
      bubbles: true,
      cancelable: true,
    });

    this.element.dispatchEvent(submitEvent);

    // If event wasn't prevented, submit the form if it exists
    if (!submitEvent.defaultPrevented) {
      const form = this.element.closest('form');
      if (form) {
        form.requestSubmit();
      } else {
        // For preview/demo purposes
        alert('Form submitted! (This is just a preview)');
      }
    }
  }
}

